// Deal Pipeline spec §3.6 — lookup catalogs surfaced in modals.
//
// IMPORTANT: a migration seeded the canonical reference tables (DeclineReasonRef,
// WithdrawalReasonRef, DocRequestReasonRef, SLATargetRef), and
// DealLookupsController serves them under /api/deal-lookups, but the UI
// does not call those endpoints: it uses the static catalog mirrored from
// 20260903000000_DealPipelineModule_v3_0_CounterpartyFoundation.cs. The
// codes here MUST stay in sync with the migration; mismatched codes will
// cause the server to reject decline/withdraw submissions with
// "ReasonCode is not a known active DeclineReasonRef" (DealService.DeclineAsync).

export interface ReasonOption {
  code: string;
  displayName: string;
}

export const DECLINE_REASONS: ReasonOption[] = [
  { code: "IndustryProhibited", displayName: "Industry prohibited" },
  { code: "BuyerCreditFails", displayName: "Buyer credit fails" },
  { code: "BuyerExperienceInsufficient", displayName: "Buyer experience insufficient" },
  { code: "DSCRBelowThreshold", displayName: "DSCR below threshold" },
  { code: "DebtToEBITDABelowThreshold", displayName: "Debt-to-EBITDA below threshold" },
  {
    code: "BusinessOperatingHistoryInsufficient",
    displayName: "Business operating history insufficient",
  },
  { code: "MissingCriticalDocs", displayName: "Missing critical documents" },
  { code: "FraudConcerns", displayName: "Fraud concerns" },
  { code: "DealStructureUnacceptable", displayName: "Deal structure unacceptable" },
  { code: "ConcentrationConcerns", displayName: "Concentration concerns" },
  { code: "GeographicLimitations", displayName: "Geographic limitations" },
  { code: "RegulatoryConcerns", displayName: "Regulatory concerns" },
  { code: "Other", displayName: "Other" },
];

export const WITHDRAWAL_REASONS: ReasonOption[] = [
  { code: "BrokerWithdrawn", displayName: "Broker withdrawn" },
  { code: "BuyerWithdrawn", displayName: "Buyer withdrawn" },
  { code: "DealCollapsed", displayName: "Deal collapsed" },
  { code: "Other", displayName: "Other" },
];

export const DOC_REQUEST_REASONS: ReasonOption[] = [
  { code: "AlreadyOnFileFromPriorDeal", displayName: "Already on file from prior deal" },
  { code: "NotApplicableToTransaction", displayName: "Not applicable to transaction" },
  { code: "WaivedByB1", displayName: "Waived by B1" },
  { code: "Rejected_Illegible", displayName: "Rejected — illegible" },
  { code: "Rejected_Outdated", displayName: "Rejected — outdated" },
  { code: "Rejected_Incomplete", displayName: "Rejected — incomplete" },
  { code: "Other", displayName: "Other" },
];

// Per-stage/sub-state SLA targets (days). Mirrored from the SLATargetRef
// seed; keep in sync with the migration. Read by ageSeverity
// (components/deals/dealFormat.ts): warning at 80% of target, breach at target.
export const SLA_TARGET_DAYS: Record<string, number> = {
  "Intake.New": 1,
  "Intake.GatheringParties": 3,
  "Intake.IntakeReview": 2,
  "Intake.ReadyForAssignment": 2,
  "PreApproval.Applications": 5,
  "PreApproval.Letters": 5,
  "PreApproval.Documents": 14,
  "PreApproval.ReadyToSubmit": 3,
  "Archive.GraceDays": 14,
};

export function slaTargetFor(stage: string, subState: string): number | undefined {
  return SLA_TARGET_DAYS[`${stage}.${subState}`];
}
